import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { buildLoginUrl } from '@/lib/redirect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useProducerEntryState } from '@/hooks/useProducerEntryState';
import { PageContainer } from '@/components/layout/PageContainer';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { track } from '@/lib/analytics';

type Phase = 'intro' | 'form';

export default function AppProducerOnboarding() {
  const { user } = useAuth();
  const { state: entryState, reason } = useProducerEntryState();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('intro');
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [email, setEmail] = useState(user?.email ?? '');
  const [message, setMessage] = useState('');

  // Invitation code modal
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);

  const hasActiveInvitation = entryState === 'in_progress' && reason === 'invitation';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate(buildLoginUrl(), { replace: true });
        return;
      }
      const { error } = await supabase
        .from('producer_interest')
        .insert({
          name: name.trim(),
          region: region.trim() || null,
          email: email.trim(),
          message: message.trim() || null,
          status: 'new',
        });
      if (error) throw error;
      // Analytics: track successful interest submission
      // Email is handled by DB trigger on producer_interest → email_outbox
      track('host_interest_submitted', {} as Record<string, never>);
      navigate('/app/producer/onboarding/success', { replace: true });
    } catch {
      navigate('/app/producer/onboarding/success', { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  const handleInviteSubmit = async () => {
    const code = inviteCode.trim();
    if (!code || !user) return;
    setInviteError('');
    setInviteSubmitting(true);

    try {
      // Hash the token with SHA-256
      const encoder = new TextEncoder();
      const data = encoder.encode(code);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const tokenHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      const { data: result, error } = await supabase.rpc('consume_invitation', {
        p_token_hash: tokenHash,
        p_user_id: user.id,
      });

      if (error) throw error;

      const row = Array.isArray(result) ? result[0] : result;
      if (row?.error_code) {
        const messages: Record<string, string> = {
          'INVALID_TOKEN': 'Koden kunde inte hittas. Kontrollera att du angett rätt.',
          'ALREADY_CONSUMED': 'Den här inbjudan har redan använts.',
          'EXPIRED': 'Inbjudan har gått ut.',
          'REVOKED': 'Inbjudan har dragits tillbaka.',
        };
        setInviteError(messages[row.error_code] || 'Något gick fel. Försök igen.');
        return;
      }

      // Success – navigate to the producer dashboard
      setInviteOpen(false);
      if (row?.producer_id) {
        navigate(`/app/producer/farm/${row.producer_id}`, { replace: true });
      } else {
        navigate('/app/producer', { replace: true });
      }
    } catch {
      setInviteError('Något gick fel. Kontrollera koden och försök igen.');
    } finally {
      setInviteSubmitting(false);
    }
  };

  return (
    <PageContainer className="max-w-md mx-auto py-6">
      <Link
        to="/app/customer"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Tillbaka
      </Link>

      {phase === 'intro' && (
        <div className="space-y-8">
          <div className="space-y-4">
            <h1 className="font-display text-2xl text-foreground leading-tight">
              Att bli värd i Farmly
            </h1>
            <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
              <p>Farmly växer i lugn takt.</p>
              <p>
                Vi arbetar redaktionellt och lyfter platser och aktiviteter
                i rätt sammanhang.
              </p>
              <p>
                Synlighet är ingen garanti – men vi gör vårt bästa för att
                matcha rätt gäster med rätt upplevelse.
              </p>
            </div>
            <p className="text-xs text-muted-foreground/70 pt-1">
              Det tar oftast bara någon minut att skicka in ett intresse.
            </p>
          </div>

          <div className="flex flex-col items-center gap-4 pt-2">
            {hasActiveInvitation ? (
              <Button
                onClick={() => navigate('/app/customer/invitations')}
                className="h-11 w-full"
              >
                Visa din inbjudan
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => setPhase('form')}
                  className="h-11 w-full"
                >
                  Visa intresse
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <button
                  onClick={() => {
                    setInviteCode('');
                    setInviteError('');
                    setInviteOpen(true);
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-2"
                >
                  Har du en inbjudan?
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {phase === 'form' && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <p className="text-xs text-muted-foreground/60">Steg 1 av 2</p>

          <div className="space-y-1">
            <h2 className="font-display text-xl text-foreground">
              Berätta om er plats
            </h2>
            <div className="space-y-1 text-sm text-muted-foreground">
              <p>Det här är en intresseanmälan.</p>
              <p>Vi läser igenom manuellt och återkommer via e-post.</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Vad heter platsen eller verksamheten?</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="region">Var finns ni?</Label>
              <Input
                id="region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="t.ex. Gotland, Roslagen eller Österlen"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="email">E-post</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="message">
                Vad är viktigt att gäster förstår innan de kommer?
              </Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Button type="submit" disabled={submitting} className="w-full h-11">
              {submitting ? 'Skickar...' : (
                <>
                  Skicka intresse
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground/60 text-center mt-3">
              Vi delar inte din information vidare.
            </p>
          </div>
        </form>
      )}

      {/* Invitation code overlay */}
      <ResponsiveOverlay
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        title="Ange din inbjudan"
        showHeader
        desktopMaxWidth="md"
      >
        <div className="space-y-4 p-1">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Om du har fått en inbjudan via e-post kan du ange koden här.
          </p>

          <div>
            <Label htmlFor="invite-code">Inbjudningskod</Label>
            <Input
              id="invite-code"
              value={inviteCode}
              onChange={(e) => {
                setInviteCode(e.target.value);
                if (inviteError) setInviteError('');
              }}
              placeholder=""
              autoFocus
              className="mt-1"
            />
            {inviteError && (
              <p className="text-sm text-destructive mt-2">{inviteError}</p>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setInviteOpen(false)}
              className="flex-1"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleInviteSubmit}
              disabled={!inviteCode.trim() || inviteSubmitting}
              className="flex-1"
            >
              {inviteSubmitting ? 'Kontrollerar...' : 'Fortsätt'}
            </Button>
          </div>
        </div>
      </ResponsiveOverlay>
    </PageContainer>
  );
}
